import React, { useEffect, useMemo, useRef, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link } from "react-router-dom";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import EyeIcon from "../../assets/image/eye-icon.svg";
import FilterIcon from "../../assets/image/filter.svg";
import { SingleDatePanel } from "../../Components/SingleDatePicker/SingleDatePicker";
import { parseApiDate, toApiDate } from "../../utils/dateRange";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { endpoint } from "../../utils/endpoints";
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import { formatDateTime } from "../../utils/formatDateTime";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";
import TripMeterStatusBadge from "../../Components/TripMeterStatusBadge";
import { isTripMeterStopped } from "../../utils/meterSensorIssue";

const pad2 = (n) => String(n).padStart(2, "0");

const mapHistoryItem = (item) => {
    const fuelOperator = item?.fuel_operator_id;
    const fuelOperatorName =
        (typeof fuelOperator === "object" && fuelOperator?.full_name) || item?.fuel_operator_name || "-";
    const rawDate = item?.createdAt || item?.created_at || item?.date;
    const d = rawDate ? new Date(rawDate) : null;
    const isValid = d && !Number.isNaN(d.getTime());
    const dateKey = isValid
        ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
        : "";
    const veh = item?.vehicle_id;
    const registration =
        (typeof veh === "object" && veh?.registration_number) || item?.registration_number || "-";
    const pump = item?.fuel_pump_id;
    const fuelPumpName =
        (typeof pump === "object" && pump?.fuel_pump_name) || item?.fuel_pump_name || "-";
    const locationFromItem =
        (typeof item?.mipl_location_id === "object" && item?.mipl_location_id?.name) ||
        item?.mipl_location_name ||
        item?.location_name ||
        "";
    const locationFromPump =
        (typeof pump === "object" &&
            typeof pump?.mipl_location_id === "object" &&
            pump?.mipl_location_id?.name) ||
        (typeof pump === "object" && pump?.location_name) ||
        "";
    const miplLocation = locationFromItem || locationFromPump || "-";
    const qty = item?.fuel_quantity;
    const fuelFilled =
        qty != null && qty !== "" && !Number.isNaN(Number(qty))
            ? `${Number(qty).toLocaleString("en-IN")} L`
            : "-";
    return {
        id: item?._id || item?.id || "",
        date: formatDateTime(rawDate, "-"),
        dateKey,
        monthNumber: isValid ? String(d.getMonth() + 1) : "",
        vehicle: registration,
        fuelFilled,
        fuelPumpName,
        miplLocation,
        fuelOperatorName,
        meterStopped: isTripMeterStopped(item),
    };
};

const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

const mapLocation = (item) => ({
    id: item?._id || item?.id || "",
    name: item?.name || item?.location_name || "—",
});

const filterByQuery = (items, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.name.toLowerCase().includes(q));
};

const FuelFilledHistory = () => {
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState({
        totalFuel: 0,
        totalAmount: 0,
        totalVehicle: 0,
    });
    const [search, setSearch] = useState("");
    const [monthFilter, setMonthFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("");
    const [miplLocationId, setMiplLocationId] = useState("");
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterPanel, setFilterPanel] = useState("menu");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPagesFromServer, setTotalPagesFromServer] = useState(1);
    const [loading, setLoading] = useState(true);
    const filterDropdownRef = useRef(null);

    const fetchFuelPumpManagementList = async () => {
        setLoading(true);
        try {
            const response = await handleApiCall(
                "get",
                (() => {
                    const params = new URLSearchParams({
                        page: String(currentPage),
                        limit: String(itemsPerPage),
                        search: search,
                    });
                    if (monthFilter !== "all") params.set("month", monthFilter);
                    if (dateFilter) params.set("date", dateFilter);
                    if (miplLocationId) params.set("mipl_location_id", miplLocationId);
                    if (search) params.set("search", search);
                        return `${endpoint.listOfFuelPumpManagement}?${params.toString()}`;
                })(),
                {}
            );
            if (response?.data?.code === 200) {
                const data = response?.data?.data || {};
                const fromFuelData = Array.isArray(data.fuel_data) ? data.fuel_data : [];
                const fromList = Array.isArray(data.list) ? data.list : [];
                const fromHistory =
                    Array.isArray(data.fuel_history_data) ? data.fuel_history_data : [];
                const list =
                    fromFuelData.length > 0
                        ? fromFuelData
                        : fromList.length > 0
                          ? fromList
                          : fromHistory;
                const mapped = list.map(mapHistoryItem);
                setRows(mapped);
                const total =
                    typeof data.total === "number"
                        ? data.total
                        : data.totalCount ?? mapped.length;
                setTotalRecords(total);
                const pages =
                    typeof data.total_pages === "number" && data.total_pages >= 1
                        ? data.total_pages
                        : Math.max(1, Math.ceil(total / itemsPerPage) || 1);
                setTotalPagesFromServer(pages);
                setStats({
                    totalFuel: data?.stats?.total_fuel ?? 0,
                    totalAmount: data?.stats?.total_amount ?? 0,
                    totalVehicle: data?.stats?.total_vehicle ?? 0,
                });
            } else {
                // const errMsg =
                //     response?.response?.data?.errorMessage ||
                //     response?.response?.data?.message ||
                //     response?.data?.message ||
                //     "Failed to load fuel filled history";
                // notifyError(errMsg);
                notifyError(getApiErrorMessage(response, 'Failed to load fuel filled history'));
                setRows([]);
                setTotalRecords(0);
                setTotalPagesFromServer(1);
                setStats({ totalFuel: 0, totalAmount: 0, totalVehicle: 0 });
            }
        } catch {
            notifyError("Failed to load fuel filled history");
            setRows([]);
            setTotalRecords(0);
            setTotalPagesFromServer(1);
            setStats({ totalFuel: 0, totalAmount: 0, totalVehicle: 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFuelPumpManagementList();
    }, [currentPage, itemsPerPage, monthFilter, dateFilter, search, miplLocationId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setIsFilterOpen(false);
                setFilterPanel("menu");
                setLocationSearch("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isFilterOpen || filterPanel !== "location") return undefined;
        let cancelled = false;
        const loadLocations = async () => {
            setLocationsLoading(true);
            try {
                const res = await handleApiCall("get", endpoint.listOfAlllocation, {});
                if (cancelled) return;
                if (res?.data?.code === 200) {
                    const d = res?.data?.data;
                    const raw = Array.isArray(d)
                        ? d
                        : Array.isArray(d?.location_data)
                          ? d.location_data
                          : Array.isArray(d?.locations)
                            ? d.locations
                            : [];
                    setLocations(raw.map(mapLocation).filter((x) => x.id));
                } else {
                    notifyError(getApiErrorMessage(res, "Failed to load locations"));
                    setLocations([]);
                }
            } catch {
                if (!cancelled) {
                    notifyError("Failed to load locations");
                    setLocations([]);
                }
            } finally {
                if (!cancelled) setLocationsLoading(false);
            }
        };
        loadLocations();
        return () => {
            cancelled = true;
        };
    }, [isFilterOpen, filterPanel]);

    const filteredLocations = useMemo(
        () => filterByQuery(locations, locationSearch),
        [locations, locationSearch]
    );

    const filteredRows = useMemo(() => {
        const q = search.toLowerCase().trim();
        return rows.filter((item) => {
            const matchesSearch =
                !q ||
                item.date.toLowerCase().includes(q) ||
                item.vehicle.toLowerCase().includes(q) ||
                item.fuelPumpName.toLowerCase().includes(q) ||
                item.miplLocation.toLowerCase().includes(q);
            const matchesMonth = monthFilter === "all" || item.monthNumber === monthFilter;
            const matchesDate = !dateFilter || item.dateKey === dateFilter;
            return matchesSearch && matchesMonth && matchesDate;
        });
    }, [rows, search, monthFilter, dateFilter]);

    const isLocalFilterActive = Boolean(
        clampSearchInput(search).trim() || monthFilter !== "all" || dateFilter
    );
    const hasActiveFilters = Boolean(
        monthFilter !== "all" || dateFilter || miplLocationId
    );

    const totalPages = isLocalFilterActive ? 1 : Math.max(1, totalPagesFromServer);
    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, monthFilter, dateFilter, miplLocationId]);

    const handleLocationSelect = (id) => {
        setMiplLocationId(id);
        setIsFilterOpen(false);
        setFilterPanel("menu");
        setLocationSearch("");
    };

    return (
        <>
            <div className="tab-pane-content mt-2.5 md:mt-7">
                {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500 mb-1">Total Fuel</p>
                        <h3 className="text-xl font-UbuntuMedium mb-0">{stats.totalFuel}</h3>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500 mb-1">Total Amount</p>
                        <h3 className="text-xl font-UbuntuMedium mb-0">{stats.totalAmount}</h3>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500 mb-1">Total Vehicles</p>
                        <h3 className="text-xl font-UbuntuMedium mb-0">{stats.totalVehicle}</h3>
                    </div>
                </div> */}

                <div className="flex gap-2.5 !mb-3 md:mb-4 items-center flex-wrap">
                    <input
                        type="text"
                        placeholder="Search..."
                        className="md:h-[42px] h-[38px] w-[210px] border px-3 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm flex-1"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />
                    <div className="relative" ref={filterDropdownRef}>
                        <button
                            type="button"
                            className="inline-flex md:h-[42px] md:w-[42px] h-[36px] w-[36px] items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                            onClick={() => {
                                setIsFilterOpen((prev) => {
                                    if (prev) setFilterPanel("menu");
                                    return !prev;
                                });
                            }}
                            title="Filters"
                        >
                            <img src={FilterIcon} alt="Filter" className="w-4 h-4 invert grayscale brightness-100"  />
                        </button>
                        {isFilterOpen && (
                            <div className="absolute right-0 top-[calc(100%+6px)] z-20 rounded-lg bg-white shadow-lg border">
                                {filterPanel === "menu" && (
                                    <div className="min-w-[170px] py-1">
                                        <button
                                            type="button"
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between text-sm"
                                            onClick={() => setFilterPanel("location")}
                                        >
                                            <span>Location</span>
                                            <span>&gt;</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between text-sm"
                                            onClick={() => setFilterPanel("month")}
                                        >
                                            <span>Month</span>
                                            <span>&gt;</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between text-sm"
                                            onClick={() => setFilterPanel("date")}
                                        >
                                            <span>Date</span>
                                            <span>&gt;</span>
                                        </button>
                                    </div>
                                )}
                                {filterPanel === "location" && (
                                    <div className="border-t border-gray-100 px-3 py-2 min-w-[240px]">
                                        <button
                                            type="button"
                                            className="mb-2 text-xs text-primary hover:underline"
                                            onClick={() => {
                                                setFilterPanel("menu");
                                                setLocationSearch("");
                                            }}
                                        >
                                            ← Back
                                        </button>
                                        <input
                                            type="search"
                                            placeholder="Search"
                                            value={locationSearch}
                                            maxLength={SEARCH_MAX_LENGTH}
                                            onChange={(e) =>
                                                setLocationSearch(clampSearchInput(e.target.value))
                                            }
                                            className="searchIcon mb-2 w-full rounded border border-primary/30 px-2 py-1.5 text-sm outline-none focus:border-primary"
                                        />
                                        <div className="max-h-40 overflow-y-auto">
                                            {locationsLoading ? (
                                                <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                    Loading…
                                                </p>
                                            ) : filteredLocations.length === 0 ? (
                                                <p className="mb-0 px-1 py-2 text-xs text-gray-500">
                                                    No locations found
                                                </p>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                            !miplLocationId
                                                                ? "font-UbuntuMedium text-primary"
                                                                : "text-gray-800"
                                                        }`}
                                                        onClick={() => handleLocationSelect("")}
                                                    >
                                                        All locations
                                                    </button>
                                                    {filteredLocations.map((loc) => (
                                                        <button
                                                            key={loc.id}
                                                            type="button"
                                                            className={`block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-primary/5 ${
                                                                miplLocationId === loc.id
                                                                    ? "font-UbuntuMedium text-primary"
                                                                    : "text-gray-800"
                                                            }`}
                                                            onClick={() =>
                                                                handleLocationSelect(loc.id)
                                                            }
                                                        >
                                                            {loc.name}
                                                        </button>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {filterPanel === "month" && (
                                    <div className="p-3 min-w-[200px]">
                                        <button
                                            type="button"
                                            className="mb-2 text-xs text-primary hover:underline"
                                            onClick={() => setFilterPanel("menu")}
                                        >
                                            ← Back
                                        </button>
                                        <label className="mb-1 block text-xs text-gray-500">Month</label>
                                        <select
                                            className="h-9 w-full rounded border px-2 text-sm outline-none focus:border-primary"
                                            value={monthFilter}
                                            onChange={(e) => {
                                                setMonthFilter(e.target.value);
                                                setIsFilterOpen(false);
                                                setFilterPanel("menu");
                                            }}
                                        >
                                            <option value="all">All Months</option>
                                            {monthOptions.map((m) => (
                                                <option key={m.value} value={m.value}>
                                                    {m.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {filterPanel === "date" && (
                                    <div className="p-3">
                                        <SingleDatePanel
                                            selected={parseApiDate(dateFilter)}
                                            onChange={(date) => {
                                                if (!date) return;
                                                setDateFilter(toApiDate(date));
                                                setIsFilterOpen(false);
                                                setFilterPanel("menu");
                                            }}
                                            maxDate={new Date()}
                                        />
                                        {dateFilter && (
                                            <div className="mt-3 border-t border-gray-100 pt-3">
                                                <button
                                                    type="button"
                                                    className="w-full rounded border px-3 py-1.5 text-xs hover:bg-gray-50"
                                                    onClick={() => {
                                                        setDateFilter("");
                                                        setFilterPanel("menu");
                                                    }}
                                                >
                                                    Clear date
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {hasActiveFilters && (
                        <button
                            type="button"
                            className="md:h-[42px] h-[38px] rounded-full bg-[#020777] text-white md:px-5 !px-4 md:text-sm text-xs font-UbuntuMedium"
                            onClick={() => {
                                setMonthFilter("all");
                                setDateFilter("");
                                setMiplLocationId("");
                                setLocationSearch("");
                                setFilterPanel("menu");
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Date</th>
                                    <th>Vehicle</th>
                                    <th>Fuel Filled</th>
                                    <th>Fuel Operator Name</th>
                                    <th>Fuel Pump Name</th>
                                    <th>MIPL Location</th>
                                    <th>Meter</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-4 text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                                {!loading && filteredRows.map((row, index) => (
                                    <tr key={row.id || index}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{row.date}</td>
                                        <td>{row.vehicle}</td>
                                        <td>{row.fuelFilled}</td>
                                        <td>{row.fuelOperatorName}</td>
                                        <td>{row.fuelPumpName}</td>
                                        <td>{row.miplLocation}</td>
                                        <td className="whitespace-nowrap">
                                            <TripMeterStatusBadge stopped={row.meterStopped} />
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <Link to={`/fuel-filled-history/view?fuel_form_id=${encodeURIComponent(row.id || "")}`}>
                                                    <img src={EyeIcon} className="h-7 min-w-[22px]" />
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && filteredRows.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-4 text-gray-500">
                                            No records found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex gap-x-4 items-center mt-4">
                    <select
                        className="p-2 shadow-sm outline-none max-[767px]:text-xs"
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {isLocalFilterActive
                            ? `${filteredRows.length} records found`
                            : totalRecords === 0
                                ? "0 records found"
                                : `${displayFrom}-${displayTo} of ${totalRecords} records found`}
                    </div>
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={loading || currentPage === 1 || isLocalFilterActive}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage === 1 ? "" : "cursor-pointer"}`}
                        >
                            <img src={LeftAngle} className="invert grayscale brightness-100" />
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">of {totalPages}</span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={loading || currentPage === totalPages || isLocalFilterActive}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage === totalPages ? "" : "cursor-pointer"}`}
                        >
                            <img src={RightAngle} className="invert grayscale brightness-100" />
                        </button>
                    </div>
                </div>
            </div>
            </>
    );
};

export default FuelFilledHistory;